import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomainDefinitionService } from "../../builder/services/domain-definition.service";

import { Observable, of } from 'rxjs';
import { map, tap } from "rxjs/operators";

import { FinancialInstrument, Insurance, FinancialAssuranceSummary, InsurancePolicy, FinancialInstrumentType, ExpiringFinancialInstrument, FinancialStatement, ExpiringInsurancePolicy, ExpiringInsuranceDetailed, StratigraphicWellFinancialAssuranceView, BondingReportModel } from '../models/financialAssurance.model';
import { InsuranceSearchCriteria } from '../../admin/models/admin-reports.model';


@Injectable()
export class FinancialAssuranceService {

    static readonly BaseUrl = '/api/financial';
    
    static readonly TemplateIds = [
        'nvE7GRWnvEcdPo1Xic',
        'nGTt2RanGTCqd12XYS',
        '0R6yx'
    ];

    private _typeDescriptions: any[];

    get TypeDescriptions(): Observable<any[]> {
        if (this._typeDescriptions != null) return of(this._typeDescriptions);
        else return this._domain.getValues("TYP_INST").pipe(
            map((data: any) => {
                let items: any[] = [];
                data.forEach(d => {
                    let item = {
                        id: d.Code,
                        description: d.Definition
                    };
                    items.push(item);
                });
                return items;
            }),
            tap((data: any[]) => {
                this._typeDescriptions = data;
            })
        );
    }

    constructor(public http: HttpClient, private _domain: DomainDefinitionService) {
    }

    getActiveFinancialStatement(orgId: string): Observable<FinancialStatement> {
        return this.http.get<FinancialStatement>(`${FinancialAssuranceService.BaseUrl}/bonds/active-financial-statement/${orgId}`);
    }

    getWellActiveFinancialInstrument(wellId: string): Observable<FinancialInstrument> {
        return this.http.get<FinancialInstrument>(`${FinancialAssuranceService.BaseUrl}/bond/well/${wellId}`);
    }

    getBond(id: string): Observable<FinancialInstrument> {
        return this.http.get<FinancialInstrument>(`${FinancialAssuranceService.BaseUrl}/bond/${id}`);
    }

    getBonds(orgId: string): Observable<FinancialInstrument[]> {
        return this.http.get<FinancialInstrument[]>(`${FinancialAssuranceService.BaseUrl}/bonds/${orgId}`);
    }

    getBondsExpiringSoon(timeFrame: number): Observable<ExpiringFinancialInstrument[]> {
        return this.http.get<ExpiringFinancialInstrument[]>(`${FinancialAssuranceService.BaseUrl}/bonds/expiring-soon?timeframeDays=${timeFrame || 30}`);
    }

    saveFinancialStatement(financialInstrument: FinancialStatement): Observable<FinancialStatement> {
        return this.http.post<FinancialStatement>(`${FinancialAssuranceService.BaseUrl}/bond`, financialInstrument);
    }

    getInsurance(id: string): Observable<Insurance> {
        return this.http.get<Insurance>(`${FinancialAssuranceService.BaseUrl}/insurance/${id}`);
    }

    getInsurances(orgId: string): Observable<Insurance[]> {
        return this.http.get<Insurance[]>(`${FinancialAssuranceService.BaseUrl}/insurances/${orgId}`);
    }

    getInsurancePoliciesExpiringSoon(timeFrame: number): Observable<ExpiringInsurancePolicy[]> {
        return this.http.get<ExpiringInsurancePolicy[]>(`${FinancialAssuranceService.BaseUrl}/insurance-policy/expiring-soon?timeframeDays=${timeFrame || 30}`);
    }

    getInsuranceOwnersExpiringSoon(criteria: InsuranceSearchCriteria): Observable<ExpiringInsuranceDetailed[]> {
        return this.http.post<ExpiringInsuranceDetailed[]>(`${FinancialAssuranceService.BaseUrl}/insurance/owners-expiring-soon`, criteria);
    }

    getStratigraphicBondingSummary(): Observable<StratigraphicWellFinancialAssuranceView[]> {
        return this.http.get<StratigraphicWellFinancialAssuranceView[]>(`${FinancialAssuranceService.BaseUrl}/stratigraphic-compliance`);
    }

    getSummaries(): Observable<FinancialAssuranceSummary[]> {
        return this.http.get<FinancialAssuranceSummary[]>(`${FinancialAssuranceService.BaseUrl}/summary`);
    }

    getSummary(orgId: string): Observable<FinancialAssuranceSummary> {
        return this.http.get<FinancialAssuranceSummary>(`${FinancialAssuranceService.BaseUrl}/summary/${orgId}`);
    }

    //getTypeDescription(typeCode: string): string {
    //    const desc = FinancialAssuranceService.TypeDescriptions.find(x => x.id.toLowerCase() === typeCode.toLowerCase()).description;
    //    return desc || '';
    //}


    getLowestCumulativeBondRequirement(summary: FinancialAssuranceSummary): FinancialInstrument {
        let result: FinancialInstrument = {
            amount: 0,
            id: '',
            organizationId: '',
            typeCode: FinancialInstrumentType.Cash
        };

        //Pull the lowest item, excluding the domestic cash fees since they're not always a complete amount
        if (summary && summary.requiredCumulativeFinancialInstruments && summary.requiredCumulativeFinancialInstruments.length > 0) {
            result = summary.requiredCumulativeFinancialInstruments.filter(x => x.typeCode != FinancialInstrumentType.DomesticCashFee).reduce((prev, curr) => prev.amount < curr.amount ? prev : curr);
        }
        return result;
    }
}

